import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { API_BASE_URL } from "../api/client";

/**
 * Whitelabel-Branding (Phase 1)
 *
 * Beim App-Start wird das Branding fuer den aktuellen Host
 * (oder via ?branding=<slug> -> localStorage gespeicherten Slug)
 * vom Backend geladen und als CSS-Variablen + Logo/Title/Favicon
 * angewendet. Faellt der Lookup leer aus, gilt das Default-Branding.
 */

const DEFAULT_BRANDING = Object.freeze({
  primary_color: "#081f4b",
  secondary_color: "#0055AA",
  accent_color: "#1a1f3a",
  logo_url: null,
  logo_url_dark: null,
  favicon_url: null,
  app_name: "DATA optimizer",
  custom_domain: null,
  company_id: null,
  company_name: null,
  slug: null,
});

const SLUG_STORAGE_KEY = "branding_slug_override";
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  reload: () => {},
  setSlugOverride: () => {},
  clearSlugOverride: () => {},
});

function readSlugFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = (params.get("branding") || "").trim().toLowerCase();
    if (!value) return null;
    if (value === "default" || value === "reset") return "__reset__";
    if (!SLUG_PATTERN.test(value)) return null;
    return value;
  } catch {
    return null;
  }
}

function readSlugFromStorage() {
  try {
    const value = window.localStorage.getItem(SLUG_STORAGE_KEY);
    if (!value) return null;
    const trimmed = value.trim().toLowerCase();
    return SLUG_PATTERN.test(trimmed) ? trimmed : null;
  } catch {
    return null;
  }
}

function persistSlug(slug) {
  try {
    if (slug) window.localStorage.setItem(SLUG_STORAGE_KEY, slug);
    else window.localStorage.removeItem(SLUG_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function applyBrandingToDocument(branding) {
  const root = document.documentElement;
  if (branding.primary_color) {
    root.style.setProperty("--color-primary", branding.primary_color);
  } else {
    root.style.removeProperty("--color-primary");
  }
  if (branding.secondary_color) {
    root.style.setProperty("--color-secondary", branding.secondary_color);
  } else {
    root.style.removeProperty("--color-secondary");
  }
  if (branding.accent_color) {
    root.style.setProperty("--color-accent", branding.accent_color);
  } else {
    root.style.removeProperty("--color-accent");
  }

  if (branding.favicon_url) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.favicon_url;
  }

  // Tab-Title wird von App.js per i18n + branding.app_name gesetzt;
  // hier bewusst nichts tun, um Doppel-Suffixe zu vermeiden.
}

async function fetchBranding({ slug, host }) {
  if (!API_BASE_URL) return DEFAULT_BRANDING;
  try {
    const url = slug
      ? `${API_BASE_URL}/api/v1/branding/by-slug/${encodeURIComponent(slug)}`
      : `${API_BASE_URL}/api/v1/branding/by-host?host=${encodeURIComponent(
          host
        )}`;
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return DEFAULT_BRANDING;
    const data = await res.json();
    return { ...DEFAULT_BRANDING, ...(data?.branding || {}) };
  } catch {
    return DEFAULT_BRANDING;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [overrideSlug, setOverrideSlug] = useState(() => {
    const fromUrl = readSlugFromUrl();
    if (fromUrl === "__reset__") {
      persistSlug(null);
      return null;
    }
    if (fromUrl) {
      persistSlug(fromUrl);
      return fromUrl;
    }
    return readSlugFromStorage();
  });

  const load = useCallback(async () => {
    setLoading(true);
    const host =
      typeof window !== "undefined" ? window.location.hostname : "";
    const result = await fetchBranding({ slug: overrideSlug, host });
    setBranding(result);
    applyBrandingToDocument(result);
    setLoading(false);
  }, [overrideSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const setSlugOverride = useCallback((slug) => {
    const normalized = (slug || "").trim().toLowerCase();
    if (!normalized || !SLUG_PATTERN.test(normalized)) return;
    persistSlug(normalized);
    setOverrideSlug(normalized);
  }, []);

  const clearSlugOverride = useCallback(() => {
    persistSlug(null);
    setOverrideSlug(null);
  }, []);

  const value = useMemo(
    () => ({
      branding,
      loading,
      reload: load,
      setSlugOverride,
      clearSlugOverride,
    }),
    [branding, loading, load, setSlugOverride, clearSlugOverride]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
